import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
    selector: 'app-pdf-search',
    templateUrl: './pdf-search.component.html',
    styleUrls: ['./pdf-search.component.scss'],
    standalone: false
})
export class PdfSearchComponent implements OnInit, OnDestroy, OnChanges {
  @Input() searchText: string = '';
  @Input() searchPerformed = false;
  @Input() searchLoading = false;
  @Input() currentMatchNumber = 0;
  @Input() totalMatches = 0;
  @Input() disabled = false;
  @Input() searchError: string = '';
  @Input() matchNote: string = '';

  @Output() searchTextChange = new EventEmitter<string>();
  @Output() searchSubmit = new EventEmitter<void>();
  @Output() goToPrevious = new EventEmitter<void>();
  @Output() goToNext = new EventEmitter<void>();
  @Output() clearSearch = new EventEmitter<void>();

  // Internal state for debounced search
  private searchSubject = new Subject<string>();
  private subscription: Subscription = new Subscription();
  
  // Waiting state - separate from loading (actual search in progress)
  isWaiting = false;

  @ViewChild('searchInput', { static: false }) searchInputRef!: ElementRef<HTMLInputElement>;

  private scrollInputToEnd(): void {
    if (this.searchInputRef?.nativeElement) {
      const input = this.searchInputRef.nativeElement;
      input.scrollLeft = input.scrollWidth;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['searchPerformed']?.currentValue === true) {
      setTimeout(() => this.scrollInputToEnd(), 0);
    }
  }

  /** True when the value is missing or only whitespace (aligned with submit/Enter). */
  private isSearchFieldEmpty(text?: string): boolean {
    const t = text !== undefined ? text : this.searchText;
    return (t || '').trim().length === 0;
  }

  ngOnInit() {
    this.subscription.add(
      this.searchSubject.pipe(
        distinctUntilChanged(),
        debounceTime(2000),
        filter(text => !this.isSearchFieldEmpty(text))
      ).subscribe(() => {
        this.isWaiting = false;
        if (!this.disabled && !this.isSearchFieldEmpty()) {
          this.searchSubmit.emit();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  handleTextChange(value: string) {
    this.searchTextChange.emit(value);
    
    // Reset waiting state - we don't show spinner during debounce period
    this.isWaiting = false;
    
    // Trigger debounced search
    this.searchSubject.next(value);
  }

  onEnter(event: Event) {
    event.preventDefault();
    if (this.disabled) {
      return;
    }

    if (this.searchPerformed && this.totalMatches > 0) {
      this.goToNext.emit();
    } else if (!this.disabled && !this.isSearchFieldEmpty()) {
      this.searchSubject.next('');
      this.searchSubmit.emit();
    }
  }

  submit() {
    if (this.disabled || this.isSearchFieldEmpty()) {
      return;
    }

    this.searchSubject.next('');
    this.searchSubmit.emit();
  }

  prev() {
    this.goToPrevious.emit();
  }

  next() {
    this.goToNext.emit();
  }

  clear() {
    this.clearSearch.emit();
  }

  public resetSearchSubject(): void {
    this.searchSubject.next('');
  }

}

